
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

import { Link } from 'react-router-dom';

export default function Home() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Sistema de Aumento de Vendas | 7deOuros';
    
    // Add Schema.org JSON-LD for homepage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Sistema de Aumento de Vendas",
      "description": "Crescimento mensurável em 60-90 dias para empresas médias. Pipeline previsível, produtividade 2x, ciclo 50% menor.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://setedeouros.com.br'}/`,
      "mainEntity": {
        "@type": "Service",
        "name": "Sistema de Aumento de Vendas",
        "description": "Metodologia validada em 300+ implementações com garantia de resultado",
        "provider": {
          "@type": "Organization",
          "name": "7deOuros"
        }
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Text Focus */}
      <section className="relative bg-gradient-to-br from-gray-900 via-red-900 to-red-800 text-white py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), 
                             radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%), 
                             radial-gradient(circle at 40% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)`
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">            
            {/* Main Headline */}
            <h1 className="text-5xl lg:text-7xl font-bold mb-8 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Sistema de <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                Aumento de Vendas
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl lg:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed">
              Mais receita em <span className="text-orange-400 font-semibold">60-90 dias</span> com o mesmo time que você já tem
            </p>
            
            {/* Social Proof Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-12 max-w-2xl mx-auto border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-orange-400 mb-2">87%</div>
                  <p className="text-sm text-gray-300">bateram a meta trimestral</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-orange-400 mb-2">+35%</div>
                  <p className="text-sm text-gray-300">aumento médio em 60 dias</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-orange-400 mb-2">-50%</div>
                  <p className="text-sm text-gray-300">redução no ciclo</p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm text-gray-300">
                  <span className="text-orange-400 font-semibold">Piloto com cobrança condicionada ao resultado</span>
                </p>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
              <a
                href="https://wa.me/5544988047699?text=Ol%C3%A1%21%20Quero%20fazer%20uma%20an%C3%A1lise%20gratuita%20do%20meu%20funil%20de%20vendas.%20Quando%20podemos%20conversar%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-orange-500 hover:bg-orange-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 px-8 py-4 text-lg rounded-xl"
              >
                <i className="ri-search-line mr-2"></i>
                Análise Gratuita do Funil
              </a>
              <a
                href="/resultados"
                className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-white/20 backdrop-blur-sm text-white border border-white/30 hover:bg-white/30 hover:scale-105 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl"
              >
                <i className="ri-play-circle-line mr-2"></i>
                Ver Resultados
              </a>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center space-x-8 text-gray-400 text-sm">
              <div className="flex items-center">
                <i className="ri-shield-check-line mr-2 text-green-400"></i>
                Garantia de Resultado
              </div>
              <div className="flex items-center">
                <i className="ri-money-dollar-circle-line mr-2 text-orange-400"></i>
                ROI Garantido
              </div>
              <div className="flex items-center">
                <i className="ri-team-line mr-2 text-blue-400"></i>
                +300 Implementações
              </div>
            </div>
            
          </div>
        </div>
        
        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-1 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 64H1440V24C1440 24 1200 0 720 0C240 0 0 24 0 24V64Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Value Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              Resultados Que Você Pode Esperar
            </h2>
            <p className="text-xl text-gray-600">Transformação real do seu time comercial</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-8 rounded-2xl border border-red-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Pipeline Previsível</h3>
                  <p className="text-gray-700">Resultados visíveis em poucas semanas com processo estruturado</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl border border-orange-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-rocket-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Produtividade 2x</h3>
                  <p className="text-gray-700">Dobrar a produtividade com a mesma equipe que você já tem</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-2xl border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-timer-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Ciclo 50% Mais Rápido</h3>
                  <p className="text-gray-700">Acelerar vendas com processo otimizado e IA integrada</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border border-blue-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Tecnologia Integrada</h3>
                  <p className="text-gray-700">Processo primeiro, depois tecnologia com site e agente IA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Metodologia */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nossa Metodologia
            </h2>
            <p className="text-xl text-gray-300">4 passos para transformar suas vendas</p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-red-600 hidden lg:block"></div>
            
            <div className="space-y-12">
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
                  <div className="bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto lg:mx-0">
                    1
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-center lg:text-left">Diagnóstico</h3>
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                    72 horas
                  </div>
                  <p className="text-gray-300 text-center lg:text-left">
                    Identificamos 3 métricas decisivas, mapeamos gargalos e criamos plano estruturado personalizado
                  </p>
                </div>
                <div className="lg:w-1/2 lg:pl-12">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20professional%20analyzing%20sales%20data%20and%20metrics%20on%20computer%20screen%20in%20modern%20office%2C%20diagnostic%20charts%20and%20graphs%20visible%2C%20clean%20corporate%20environment%20with%20professional%20lighting%2C%20focused%20consultant%20reviewing%20business%20performance%20indicators%2C%20red%20accent%20colors%20in%20the%20interface&width=500&height=300&seq=diagnostico-step&orientation=landscape"
                    alt="Diagnóstico"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row-reverse items-center">
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto lg:mx-0">
                    2
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-center lg:text-left">Implementação</h3>
                  <div className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                    15 dias
                  </div>
                  <p className="text-gray-300 text-center lg:text-left">
                    Estabelecemos rotina, cadência operacional, playbook completo, site otimizado e IA implementada
                  </p>
                </div>
                <div className="lg:w-1/2 lg:pr-12">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20team%20implementing%20sales%20processes%20with%20modern%20technology%20setup%2C%20professionals%20working%20with%20laptops%20and%20tablets%20in%20contemporary%20office%2C%20collaborative%20workspace%20with%20clean%20design%2C%20orange%20and%20red%20accent%20colors%2C%20implementation%20phase%20of%20business%20transformation&width=500&height=300&seq=implementacao-step&orientation=landscape"
                    alt="Implementação"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
                  <div className="bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto lg:mx-0">
                    3
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-center lg:text-left">Operação</h3>
                  <div className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                    Diária
                  </div>
                  <p className="text-gray-300 text-center lg:text-left">
                    Mais reuniões produtivas, respostas rápidas aos prospects e aumento nos fechamentos
                  </p>
                </div>
                <div className="lg:w-1/2 lg:pl-12">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Sales%20team%20in%20daily%20operation%20meeting%20discussing%20prospects%20and%20deals%2C%20modern%20conference%20room%20with%20digital%20displays%20showing%20sales%20metrics%2C%20professional%20business%20environment%20with%20engaged%20team%20members%2C%20blue%20accent%20colors%20in%20presentation%20materials&width=500&height=300&seq=operacao-step&orientation=landscape"
                    alt="Operação"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row-reverse items-center">
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-red-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto lg:mx-0">
                    4
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-center lg:text-left">Escala</h3>
                  <div className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-medium inline-block mb-4">
                    Expansão
                  </div>
                  <p className="text-gray-300 text-center lg:text-left">
                    Ampliamos tudo que funcionou na primeira equipe para todo seu time comercial
                  </p>
                </div>
                <div className="lg:w-1/2 lg:pr-12">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Large%20sales%20team%20celebrating%20success%20and%20growth%20in%20modern%20corporate%20office%2C%20expanded%20team%20with%20multiple%20workstations%2C%20upward%20trending%20charts%20on%20wall%20displays%2C%20professional%20celebration%20of%20business%20scaling%2C%20green%20accent%20colors%20representing%20growth&width=500&height=300&seq=escala-step&orientation=landscape"
                    alt="Escala"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              Por Que Escolher a 7deOuros
            </h2>
            <p className="text-xl text-gray-600">Diferenciais que garantem seus resultados</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">ROI Garantido</h3>
              <p className="text-gray-600">Resultados mensuráveis ou você não paga o valor completo</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Implementação Rápida</h3>
              <p className="text-gray-600">Primeiros resultados visíveis em poucas semanas</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-verified-badge-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Metodologia Validada</h3>
              <p className="text-gray-600">Testada e aprovada em mais de 300 implementações</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-5 00 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-settings-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Personalização Estratégica</h3>
              <p className="text-gray-600">Solução adaptada especificamente ao seu negócio</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Escala Previsível</h3>
              <p className="text-gray-600">Crescimento sustentável e escalável para toda empresa</p>
            </div>
            
            <div className="text-center group hover:scale-105 transition-transform duration-300">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Parceria Colaborativa</h3>
              <p className="text-gray-600">Trabalhamos lado a lado com seu time interno</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Garantia CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-sm rounded-2xl p-3 mb-8">
            <i className="ri-shield-check-line text-4xl"></i>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Garantia de Resultado
          </h2>
          <p className="text-xl lg:text-2xl text-red-100 mb-8 leading-relaxed">
            Implementamos em 1 equipe. Se não houver resultado mensurável em 60 dias, 
            você não paga o valor completo.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
            <a
              href="https://wa.me/5544988047699?text=Ol%C3%A1%21%20Quero%20fazer%20uma%20an%C3%A1lise%20gratuita%20do%20meu%20funil%20de%20vendas.%20Quando%20podemos%20conversar%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-white text-red-600 hover:bg-gray-100 hover:scale-105 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl"
            >
              Começar Agora
            </a>
            <a
              href="https://wa.me/5544988047699?text=Ol%C3%A1%21%20Quero%20falar%20com%20um%20especialista%20sobre%20o%20sistema%20de%20vendas.%20Quando%20podemos%20conversar%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 border-2 border-white text-white hover:bg-white hover:text-red-600 hover:scale-105 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl"
            >
              Falar com Especialista
            </a>
          </div>
          
          {/* Banner Sites + Agente */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-white mb-1">
                  Precisa de site rápido?
                </h3>
                <p className="text-red-100 text-sm">
                  Veja Sites + Agente (demo em 24–48h)
                </p>
              </div>
              <Link
                to="/sites-agente"
                className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-white text-red-600 hover:bg-gray-100 hover:scale-105 shadow-lg hover:shadow-xl px-6 py-3 text-sm rounded-lg"
              >
                <i className="ri-external-link-line mr-2"></i>
                Ver Sites + Agente
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
